// app/(app)/reports/sales.tsx
import { Button, Input } from "@/components/default/forms";
import DatePicker from "@/components/default/forms/DatePicker";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { locationApi } from "@/services/locationApi";
import { reportsApi, SalesReportInvoice } from "@/services/reportsApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchCustomers } from "@/store/slices/customerSlice";
import { fetchEmployees } from "@/store/slices/employeeSlice";
import { showAlert } from "@/utils/alert";
import { Ionicons } from "@expo/vector-icons";
import * as FileSystem from "expo-file-system/legacy";
import { useFocusEffect, useRouter } from "expo-router";
import * as Sharing from "expo-sharing";
import { useCallback, useEffect, useState } from "react";
import {
  FlatList,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function SalesReportScreen() {
  const { colors, spacing } = useAppTheme();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { customers, loading: customersLoading } = useAppSelector(
    (state) => state.customers,
  );
  const { employees, loading: employeesLoading } = useAppSelector(
    (state) => state.employees,
  );

  const isEmployee = user?.userType === "Employee";
  const isAdmin = user?.userType === "Admin" || user?.userType === "Owner";

  // Filter states
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedAreaId, setSelectedAreaId] = useState<string>("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [customerName, setCustomerName] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [employeeName, setEmployeeName] = useState("");

  // Data states
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [summary, setSummary] = useState<any>(null);
  const [invoices, setInvoices] = useState<SalesReportInvoice[]>([]);

  // Areas state
  const [areas, setAreas] = useState<{ id: number; name: string }[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(false);

  // Search states
  const [customerSearch, setCustomerSearch] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");

  // Load areas and employees on mount
  useFocusEffect(
    useCallback(() => {
      loadAreas();
      dispatch(fetchEmployees({ page: 1, limit: 100, search: "" }));
    }, []),
  );

  const loadAreas = async () => {
    setLoadingAreas(true);
    try {
      const fetchedAreas = await locationApi.getAreas();
      setAreas(fetchedAreas);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingAreas(false);
    }
  };

  // If employee list is empty, force filter to current user (if employee)
  useEffect(() => {
    if (isEmployee && employees.length === 0 && user?.id) {
      setSelectedEmployeeId(user.id.toString());
      setEmployeeName(user.ownerName || "");
    } else if (isEmployee && employees.length > 0) {
      // If employees exist, let them choose (but we may still auto-select themselves)
      // We'll auto-select themselves for convenience, but they can clear it.
      if (!selectedEmployeeId && user?.id) {
        setSelectedEmployeeId(user.id.toString());
        setEmployeeName(user.ownerName || "");
      }
    }
  }, [employees, isEmployee, user]);

  // Debounced customer search (with optional area filter)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (customerSearch !== undefined) {
        if (selectedAreaId !== "") {
          dispatch(
            fetchCustomers({
              page: 1,
              limit: 20,
              search: customerSearch,
              areaId: Number(selectedAreaId),
            }),
          );
        } else {
          dispatch(
            fetchCustomers({ page: 1, limit: 20, search: customerSearch }),
          );
        }
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [customerSearch, selectedAreaId]);

  // Debounced employee search (for all users)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (employeeSearch !== undefined) {
        dispatch(
          fetchEmployees({ page: 1, limit: 20, search: employeeSearch }),
        );
      }
    }, 500);
    return () => clearTimeout(timeout);
  }, [employeeSearch]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (invoiceNo.trim()) params.invoiceNo = invoiceNo;
      if (selectedCustomerId) params.customerId = parseInt(selectedCustomerId);
      if (selectedAreaId) params.areaId = parseInt(selectedAreaId);
      // ✅ Employee filter: send only if selected
      if (selectedEmployeeId) {
        params.createdBy = parseInt(selectedEmployeeId);
      }
      const response = await reportsApi.getSalesReport(params);
      setSummary(response.summary);
      setInvoices(response.invoices);
    } catch (error: any) {
      showAlert("Error", error.message || "Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (invoiceNo.trim()) params.invoiceNo = invoiceNo;
      if (selectedCustomerId) params.customerId = parseInt(selectedCustomerId);
      if (selectedAreaId) params.areaId = parseInt(selectedAreaId);
      if (selectedEmployeeId) {
        params.createdBy = parseInt(selectedEmployeeId);
      }
      const blob = await reportsApi.downloadSalesReportPdf(params);

      if (Platform.OS === "web") {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = (reader.result as string).split(",")[1];
          const fileUri =
            FileSystem.cacheDirectory + `sales_report_${Date.now()}.pdf`;
          await FileSystem.writeAsStringAsync(fileUri, base64, {
            encoding: FileSystem.EncodingType.Base64,
          });
          if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(fileUri);
          } else {
            showAlert("Error", "Sharing not available");
          }
        };
        reader.readAsDataURL(blob);
      }
    } catch (error: any) {
      showAlert("Error", error.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const handleCustomerSelect = (value: string, label: string) => {
    setSelectedCustomerId(value);
    setCustomerName(label);
    setCustomerSearch("");
  };

  const handleClearCustomer = () => {
    setSelectedCustomerId("");
    setCustomerName("");
    setCustomerSearch("");
  };

  const handleEmployeeSelect = (value: string, label: string) => {
    setSelectedEmployeeId(value);
    setEmployeeName(label);
    setEmployeeSearch("");
  };

  const handleClearEmployee = () => {
    setSelectedEmployeeId("");
    setEmployeeName("");
    setEmployeeSearch("");
  };

  const renderInvoiceItem = ({ item }: { item: SalesReportInvoice }) => (
    <View style={styles.invoiceCard}>
      <View style={styles.invoiceHeader}>
        <Text style={styles.invoiceNo}>{item.invoiceNo}</Text>
        <Text style={styles.invoiceDate}>
          {new Date(item.invoiceDate).toLocaleDateString()}
        </Text>
      </View>
      <Text style={styles.customerName}>{item.Customer.fullName}</Text>
      <View style={styles.amountRow}>
        <Text style={styles.amountLabel}>Grand Total:</Text>
        <Text style={styles.amountValue}>
          ₹{parseFloat(item.grandTotal).toFixed(2)}
        </Text>
      </View>
    </View>
  );

  // Compute customer options with selected customer always included
  const getCustomerOptions = () => {
    const baseOptions = customers.map((c) => ({
      label: `${c.fullName} (${c.phone})${c.Area?.name ? ` - ${c.Area.name}` : ""}`,
      value: c.id.toString(),
    }));
    if (
      selectedCustomerId &&
      !baseOptions.some((opt) => opt.value === selectedCustomerId)
    ) {
      const selectedCust = customers.find(
        (c) => c.id.toString() === selectedCustomerId,
      );
      if (selectedCust) {
        baseOptions.unshift({
          label: `${selectedCust.fullName} (${selectedCust.phone})${selectedCust.Area?.name ? ` - ${selectedCust.Area.name}` : ""}`,
          value: selectedCust.id.toString(),
        });
      } else if (customerName) {
        baseOptions.unshift({
          label: customerName,
          value: selectedCustomerId,
        });
      }
    }
    return baseOptions;
  };

  // Determine if employee filter should be disabled (hide or disable)
  // If employee list is empty, we hide the filter (or disable it).
  // For employees with empty list, they only see their own invoices, so we can hide the dropdown.
  const showEmployeeFilter = employees.length > 0;

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    backButton: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    filterRow: {
      flexDirection: "row",
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    filterButton: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: spacing.sm,
      alignItems: "center",
    },
    filterText: { fontSize: 14, color: colors.text },
    inputContainer: { marginBottom: spacing.md },
    buttonRow: {
      flexDirection: "row",
      gap: spacing.sm,
      marginBottom: spacing.lg,
    },
    summaryCard: {
      backgroundColor: colors.background,
      borderRadius: 12,
      padding: spacing.md,
      marginBottom: spacing.lg,
      borderWidth: 1,
      borderColor: colors.border,
    },
    summaryTitle: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.sm,
    },
    summaryRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: spacing.xs,
    },
    summaryLabel: { fontSize: 14, color: colors.textLight },
    summaryValue: { fontSize: 14, fontWeight: "500", color: colors.text },
    grandValue: { fontSize: 16, fontWeight: "700", color: colors.primary },
    invoiceCard: {
      backgroundColor: colors.background,
      borderRadius: 8,
      padding: spacing.md,
      marginBottom: spacing.sm,
      borderWidth: 1,
      borderColor: colors.border,
    },
    invoiceHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: spacing.xs,
    },
    invoiceNo: { fontSize: 14, fontWeight: "600", color: colors.text },
    invoiceDate: { fontSize: 12, color: colors.textLight },
    customerName: {
      fontSize: 14,
      color: colors.text,
      marginBottom: spacing.xs,
    },
    amountRow: { flexDirection: "row", justifyContent: "space-between" },
    amountLabel: { fontSize: 12, color: colors.textLight },
    amountValue: { fontSize: 12, fontWeight: "500", color: colors.primary },
    emptyText: {
      textAlign: "center",
      color: colors.textLight,
      marginTop: spacing.xxl,
    },
    clearLink: {
      alignSelf: "flex-end",
      marginBottom: spacing.md,
    },
    clearText: { color: colors.danger },
  });

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => router.back()}
          >
            <Ionicons name="arrow-back" size={24} color={colors.primary} />
            <Text style={{ color: colors.primary, marginLeft: 4 }}>Back</Text>
          </TouchableOpacity>

          <Text style={styles.title}>Sales Report</Text>

          <View style={styles.filterRow}>
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              placeholder="Start Date"
              containerStyle={{ flex: 1, marginBottom: 0 }}
            />
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              placeholder="End Date"
              containerStyle={{ flex: 1, marginBottom: 0 }}
            />
            {(startDate || endDate) && (
              <TouchableOpacity
                style={[styles.filterButton, { borderColor: colors.danger }]}
                onPress={() => {
                  setStartDate("");
                  setEndDate("");
                }}
              >
                <Text style={{ color: colors.danger }}>Clear</Text>
              </TouchableOpacity>
            )}
          </View>

          <SearchableSelect
            label="Area / Route"
            placeholder="All areas"
            options={areas.map((a) => ({
              label: a.name,
              value: a.id.toString(),
            }))}
            value={selectedAreaId}
            onValueChange={(val) => setSelectedAreaId(val)}
            loading={loadingAreas}
            containerStyle={styles.inputContainer}
            clearable
          />

          <SearchableSelect
            label="Customer"
            placeholder="Search by name or phone"
            options={getCustomerOptions()}
            value={selectedCustomerId}
            onValueChange={handleCustomerSelect}
            onSearch={setCustomerSearch}
            loading={customersLoading}
            containerStyle={styles.inputContainer}
            clearable
          />

          {/* ✅ Employee filter – visible only if employees list is not empty */}
          {showEmployeeFilter && (
            <SearchableSelect
              label="Created By (Employee)"
              placeholder="All employees"
              options={employees.map((emp) => ({
                label: `${emp.ownerName} ${emp.email ? `(${emp.email})` : ""}`,
                value: emp.id.toString(),
              }))}
              value={selectedEmployeeId}
              onValueChange={handleEmployeeSelect}
              onSearch={setEmployeeSearch}
              loading={employeesLoading}
              containerStyle={styles.inputContainer}
              clearable
            />
          )}

          <Input
            label="Invoice Number (partial match)"
            placeholder="e.g., INV-2026"
            value={invoiceNo}
            onChangeText={setInvoiceNo}
            containerStyle={styles.inputContainer}
          />

          <View style={styles.buttonRow}>
            <Button
              title="Apply Filters"
              onPress={fetchReport}
              loading={loading}
              variant="primary"
            />
            <Button
              title="Download PDF"
              onPress={handleDownloadPdf}
              loading={downloading}
              variant="outline"
            />
          </View>

          {summary && (
            <View style={styles.summaryCard}>
              <Text style={styles.summaryTitle}>Summary</Text>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Invoices:</Text>
                <Text style={styles.summaryValue}>
                  {summary.totalInvoiceCount}
                </Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Invoice Amount:</Text>
                <Text style={styles.summaryValue}>
                  ₹{parseFloat(summary.totalInvoiceAmount).toFixed(2)}
                </Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Tax Amount:</Text>
                <Text style={styles.summaryValue}>
                  ₹{parseFloat(summary.totalTaxAmount).toFixed(2)}
                </Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>Total Grand Amount:</Text>
                <Text style={[styles.summaryValue, styles.grandValue]}>
                  ₹{parseFloat(summary.totalGrandAmount).toFixed(2)}
                </Text>
              </View>
            </View>
          )}

          <FlatList
            data={invoices}
            keyExtractor={(item) => item.id.toString()}
            renderItem={renderInvoiceItem}
            scrollEnabled={false}
            ListEmptyComponent={
              !loading && !summary && invoices.length === 0 ? (
                <Text style={styles.emptyText}>
                  No invoices found. Apply filters and click "Apply Filters".
                </Text>
              ) : null
            }
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
